import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Trash2, Sparkles, Shield, ExternalLink } from 'lucide-react';
import { LEGAL_URLS } from '../../config/legalUrls';
import { useAIChat } from '../../components/AIChat/useAIChat';
import { ChatMessage } from '../../components/AIChat/ChatMessage';
import { MessageInput } from '../../components/AIChat/MessageInput';
import { SuggestedQuestions } from '../../components/AIChat/SuggestedQuestions';
import { TypingIndicator } from '../../components/AIChat/TypingIndicator';
import { QueryUsageIndicator } from '../../components/AIChat/QueryUsageIndicator';
import { PageHeader } from '../../components/common/PageHeader';

/**
 * AI Commission Assistant Page
 *
 * Full-page conversational AI interface for querying commission data.
 * Premium dark theme with orange glassmorphism styling.
 *
 * Features:
 * - Natural language queries via Vertex AI
 * - Multi-turn conversations with context
 * - Rich visualizations (charts, tables, metrics)
 * - AI-generated insights and recommendations
 * - Query usage tracking with visual indicator
 * - Australian data residency compliance
 */
const AI_CONSENT_KEY = 'valutten_ai_consent';

const AIAssistantPage: React.FC = () => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [hasAIConsent, setHasAIConsent] = useState(() => localStorage.getItem(AI_CONSENT_KEY) === 'granted');
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const {
    messages,
    isLoading,
    sendMessage,
    retryLastMessage,
    clearHistory,
  } = useAIChat();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Send pending message after consent is granted
  useEffect(() => {
    if (hasAIConsent && pendingMessage) {
      sendMessage(pendingMessage);
      setTimeout(() => setRefreshTrigger((prev) => prev + 1), 2000);
      setPendingMessage(null);
    }
  }, [hasAIConsent, pendingMessage, sendMessage]);

  const handleGrantConsent = useCallback(() => {
    localStorage.setItem(AI_CONSENT_KEY, 'granted');
    setHasAIConsent(true);
    setShowConsentModal(false);
  }, []);

  // Handle message send — gate behind consent
  const handleSendMessage = (message: string) => {
    if (!hasAIConsent) {
      setPendingMessage(message);
      setShowConsentModal(true);
      return;
    }
    sendMessage(message);
    // Refresh usage indicator after sending
    setTimeout(() => setRefreshTrigger((prev) => prev + 1), 2000);
  };

  return (
    // pb-8 = 2rem of breathing room under the footer; the chat card consumes the rest of the
    // viewport so the page itself never scrolls (only the message list does).
    <div className="flex flex-col h-full min-h-0 pb-8">
      <div className="max-w-7xl mx-auto w-full flex flex-col flex-1 min-h-0">
        <PageHeader
          icon={Sparkles}
          title="Valutten AI"
          badge="Beta"
          subtitle="Your intelligent commission data analyst"
          actions={
            <div className="flex items-center gap-2">
              <div className="bg-blue-500/10 backdrop-blur-sm px-4 py-2 rounded-xl">
                <QueryUsageIndicator refreshTrigger={refreshTrigger} />
              </div>
              {messages.length > 1 && (
                <button
                  onClick={clearHistory}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium border border-gray-600/50 text-gray-400 hover:text-white hover:bg-white/[0.04] transition-all duration-150"
                  title="Clear conversation"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Clear</span>
                </button>
              )}
            </div>
          }
        />

        {/* AI Chat Interface - Glassmorphism Card.
            min-h-0 is load-bearing: without it the flex item's default min-height:auto refuses to
            shrink below its content, so the card grew past the viewport and the PAGE scrolled
            instead of the message list, leaving dead space under the footer. The previous
            style={{ minHeight: '500px' }} was also a pixel hardcode, which apps/client/CLAUDE.md
            bans for layout sizing (REM-first). flex-1 + min-h-0 fills the screen at any height. */}
        <div className="flex-1 min-h-0 bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-700/50 overflow-hidden flex flex-col">
          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 bg-gradient-to-b from-gray-900/50 to-gray-800/50">
            {messages.map((msg) => (
              <ChatMessage
                key={msg.id}
                message={msg}
                onRetry={msg.error ? retryLastMessage : undefined}
              />
            ))}

            {/* Typing indicator */}
            {isLoading && <TypingIndicator />}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested questions (show when not loading and few messages) */}
          {!isLoading && messages.length <= 2 && (
            <SuggestedQuestions onSelect={handleSendMessage} disabled={isLoading} />
          )}

          {/* Input area */}
          <MessageInput
            onSend={handleSendMessage}
            disabled={isLoading}
            placeholder="Ask about your commissions... (e.g., 'Show me my top brokers this month')"
          />
        </div>

        {/* Minimal Footer. shrink-0 so it never steals height from the chat card. */}
        <div className="mt-4 shrink-0 text-center">
          <p className="text-xs text-gray-500">
            Powered by Claude AI • Broker names anonymised before processing
          </p>
        </div>
      </div>

      {/* AI Processing Consent Modal */}
      {showConsentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/10 rounded-xl">
                <Shield className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">AI Data Processing</h3>
            </div>
            <div className="space-y-3 text-sm text-gray-300">
              <p>
                Your questions are processed by <strong className="text-white">Anthropic (Claude AI)</strong> in the United States.
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5 shrink-0">&#10003;</span>
                  <span>Broker and lender names are <strong className="text-white">anonymised</strong> before transmission</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5 shrink-0">&#10003;</span>
                  <span>Commission data stays in <strong className="text-white">Australia</strong> (BigQuery)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5 shrink-0">&#10003;</span>
                  <span>Prompts retained by Anthropic for up to <strong className="text-white">30 days</strong> for safety</span>
                </li>
              </ul>
              <p className="text-gray-400 text-xs">
                You can revoke this consent anytime in Settings &gt; Privacy &amp; Consent.
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowConsentModal(false); setPendingMessage(null); }}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGrantConsent}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 transition-colors"
              >
                I Understand, Continue
              </button>
            </div>
            <a
              href={LEGAL_URLS.privacy}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 mt-3 text-xs text-gray-500 hover:text-gray-400 transition-colors"
            >
              Read full privacy policy <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistantPage;
